/** Yahoo Finance (unofficial, keyless): v8 chart for history/quotes, v1 search. */
import { z } from 'zod';
import { addDays, toUTC } from '../dates.js';
import { assertDateRange, codeFromSymbol, EpochSeconds, EXCHANGE_SUFFIXES, getJson, parseOrThrow, priceToCents, } from './provider.js';
const ID = 'yahoo';
const BASE = 'https://query1.finance.yahoo.com';
const Series = z.array(z.number().nullable()).nullish();
const ChartSchema = z.object({
    chart: z.object({
        result: z.array(z.object({
            meta: z.object({
                symbol: z.string(),
                currency: z.string().nullish(),
                gmtoffset: z.number().int().min(-86_400).max(86_400).nullish(),
                regularMarketPrice: z.number().nullish(),
                regularMarketTime: EpochSeconds.nullish(),
            }),
            timestamp: z.array(EpochSeconds).nullish(),
            indicators: z.object({
                quote: z.array(z.object({ open: Series, high: Series, low: Series, close: Series, volume: Series })).prefault([]),
            }),
        })).min(1),
    }),
});
const SearchItem = z.object({
    symbol: z.string(),
    shortname: z.string().nullish(),
    longname: z.string().nullish(),
    exchange: z.string().nullish(),
    quoteType: z.string().nullish(),
});
const SearchSchema = z.object({ quotes: z.array(z.unknown()).prefault([]) });
/** Yahoo exchange codes → Keel exchange codes. */
const EXCHANGES = {
    ASX: 'ASX', NMS: 'NASDAQ', NGM: 'NASDAQ', NCM: 'NASDAQ', NYQ: 'NYSE', PCX: 'NYSE', ASE: 'NYSE', BTS: 'NYSE',
    LSE: 'LSE', NZE: 'NZX', TOR: 'TSX', GER: 'XETRA',
};
const securityType = (t) => {
    switch ((t ?? '').toUpperCase()) {
        case 'EQUITY': return 'share';
        case 'ETF': return 'etf';
        case 'MUTUALFUND': return 'managed_fund';
        case 'CRYPTOCURRENCY': return 'crypto';
        default: return 'other';
    }
};
const chart = async (ctx, symbol, query) => {
    const body = await getJson(ctx, ID, `${BASE}/v8/finance/chart/${encodeURIComponent(symbol)}?${query}`);
    return parseOrThrow(ChartSchema, body, ID).chart.result[0];
};
export const createYahooProvider = (ctx) => ({
    id: ID,
    name: 'Yahoo Finance',
    requiresApiKey: false,
    async history(symbol, from, to) {
        assertDateRange(ID, from, to);
        const p1 = Math.floor(toUTC(from).getTime() / 1000);
        const p2 = Math.floor(toUTC(addDays(to, 1)).getTime() / 1000);
        const r = await chart(ctx, symbol, `period1=${p1}&period2=${p2}&interval=1d`);
        const ccy = r.meta.currency ?? 'USD';
        const offset = r.meta.gmtoffset ?? 0; // exchange-local calendar day (ASX bars open 23:00/00:00 UTC the day before)
        const q = r.indicators.quote[0];
        const at = (s, i) => s?.[i] ?? null;
        const cents = (v) => (v === null ? null : priceToCents(v, ccy).cents);
        const bars = [];
        (r.timestamp ?? []).forEach((ts, i) => {
            const close = at(q?.close, i);
            if (close === null)
                return; // Yahoo emits null bars for halts / partial days
            const date = new Date((ts + offset) * 1000).toISOString().slice(0, 10);
            if (date < from || date > to)
                return;
            bars.push({
                date, closeCents: priceToCents(close, ccy).cents,
                open: cents(at(q?.open, i)), high: cents(at(q?.high, i)), low: cents(at(q?.low, i)),
                volume: at(q?.volume, i), source: ID,
            });
        });
        return bars;
    },
    async quote(symbols) {
        const results = await Promise.all(symbols.map((s) => chart(ctx, s, 'range=1d&interval=1d')));
        return results.flatMap((r, i) => {
            const { regularMarketPrice: price, regularMarketTime: time, currency } = r.meta;
            if (price === null || price === undefined)
                return [];
            const { cents, currency: ccy } = priceToCents(price, currency ?? 'USD');
            const asOf = new Date((time ?? Math.floor(ctx.now().getTime() / 1000)) * 1000).toISOString();
            return [{ symbol: symbols[i], priceCents: cents, currency: ccy, asOf }];
        });
    },
    async search(q, opts = {}) {
        const url = `${BASE}/v1/finance/search?q=${encodeURIComponent(q)}&quotesCount=${opts.limit ?? 10}&newsCount=0`;
        const { quotes } = parseOrThrow(SearchSchema, await getJson(ctx, ID, url), ID);
        return quotes.flatMap((raw) => {
            const it = SearchItem.safeParse(raw);
            if (!it.success)
                return [];
            const exchange = EXCHANGES[it.data.exchange ?? ''] ?? it.data.exchange ?? '';
            return [{
                    symbol: it.data.symbol,
                    code: it.data.symbol.startsWith('^') ? it.data.symbol : codeFromSymbol(it.data.symbol),
                    exchange,
                    name: it.data.longname ?? it.data.shortname ?? it.data.symbol,
                    type: securityType(it.data.quoteType),
                    currency: EXCHANGE_SUFFIXES[exchange]?.currency ?? null,
                }];
        });
    },
});
//# sourceMappingURL=yahoo.js.map